import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Settings, Save, RefreshCw, History } from 'lucide-react';
import { toast } from 'sonner';
import { isAdmin } from '@/lib/auth';
import api from '@/lib/api';

export default function LeaveRules() {
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [accrualLog, setAccrualLog] = useState({ runs: [], employee_logs: [] });
  const [logLoading, setLogLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillReport, setBackfillReport] = useState(null);
  const [reconcileRunning, setReconcileRunning] = useState(false);
  const [reconcileReport, setReconcileReport] = useState(null);
  const admin = isAdmin();

  const loadLog = () => {
    setLogLoading(true);
    api.get('/api/leave/accrual/log')
      .then(r => setAccrualLog(r.data || { runs: [], employee_logs: [] }))
      .catch(console.error)
      .finally(() => setLogLoading(false));
  };

  useEffect(() => {
    api.get('/api/leave/rules').then(r => setRules(r.data)).catch(console.error).finally(() => setLoading(false));
    if (isAdmin()) loadLog();
  }, []);

  const handleSave = async () => {
    try {
      await api.put('/api/leave/rules', rules);
      toast.success('Leave rules updated');
      setEditing(false);
    } catch (e) { toast.error('Failed to update'); }
  };

  const handleRunAccrual = async () => {
    if (!window.confirm('Run monthly leave accrual now? This will credit +1 EL and +0.5 CL to every active employee for any month they have not yet been accrued for. This is safe and idempotent.')) return;
    setRunning(true);
    try {
      const r = await api.post('/api/leave/accrual/run');
      toast.success(`Accrual complete: target ${r.data.target_month} | months credited: ${r.data.accrued} | FY resets: ${r.data.fy_resets}`);
      loadLog();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Accrual failed');
    } finally {
      setRunning(false);
    }
  };

  const handleBackfill = async (dryRun) => {
    const msg = dryRun
      ? 'Run dry-run? This will scan all employees with negative leave balance and report which past leaves would be re-classified as LOP, WITHOUT writing any changes.'
      : 'Run real backfill? This re-classifies past approved leaves where balance went negative as partial-LOP. Their CL/EL/SL balances will be corrected. AFTER this, you should DELETE + REGENERATE any payroll runs in the affected months so the LOP applies to salary.';
    if (!window.confirm(msg)) return;
    setBackfillRunning(true);
    try {
      const r = await api.post('/api/leave/admin/backfill-excess', { dry_run: dryRun });
      setBackfillReport(r.data);
      toast.success(
        dryRun
          ? `Dry-run: ${r.data.employees_touched} employees would be touched.`
          : `Backfill done: ${r.data.employees_touched} employees, ${r.data.leaves_marked} leaves marked, ${r.data.total_lop_days_marked} LOP days. Affected runs: ${(r.data.affected_payroll_runs || []).join(', ') || 'none'}`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backfill failed');
    } finally {
      setBackfillRunning(false);
    }
  };

  const handleReconcileAll = async (dryRun) => {
    const msg = dryRun
      ? "Dry-run? Compares each employee's stored used/balance with their approved leaves and shows the gap. No writes."
      : "Run reconcile? Force-syncs every employee's used/balance to match their approved leaves. Use when payslip's leave_breakdown doesn't match the Leave page numbers.";
    if (!window.confirm(msg)) return;
    setReconcileRunning(true);
    try {
      const r = await api.post('/api/leave/admin/reconcile-all', { dry_run: dryRun });
      setReconcileReport(r.data);
      toast.success(`${dryRun ? 'Dry-run:' : 'Reconciled:'} ${r.data.employees_reconciled} of ${r.data.total_scanned} employees ${dryRun ? 'would be' : ''} synced`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reconcile failed');
    } finally {
      setReconcileRunning(false);
    }
  };


  if (loading) return <Skeleton className="h-64" />;
  if (!rules) return <p className="text-sm text-slate-400 p-6">No rules configured</p>;

  const RuleRow = ({ label, field, type = 'number' }) => (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <Label className="text-sm text-slate-700 font-normal">{label}</Label>
      {type === 'switch' ? (
        <Switch
          checked={rules[field]}
          onCheckedChange={v => editing && setRules({ ...rules, [field]: v })}
          disabled={!editing}
          data-testid={`rule-${field}`}
        />
      ) : (
        <Input
          type="number"
          value={rules[field] ?? ''}
          onChange={e => setRules({ ...rules, [field]: parseInt(e.target.value) || 0 })}
          disabled={!editing}
          className="w-20 h-8 text-sm text-right"
          data-testid={`rule-${field}`}
        />
      )}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="leave-rules">
      <Card className="border-slate-200">
        <CardHeader className="py-3 px-4 pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Settings className="w-4 h-4 text-slate-500" /> Leave Policy Rules
          </CardTitle>
          {admin && (
            editing ? (
              <Button size="sm" onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700 gap-1 h-8" data-testid="save-rules-btn">
                <Save className="w-3.5 h-3.5" />Save
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="h-8" data-testid="edit-rules-btn">Edit Rules</Button>
            )
          )}
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <RuleRow label="Max Casual Leaves per month" field="max_cl_per_month" />
          <RuleRow label="Max Earned Leaves per month" field="max_el_per_month" />
          <RuleRow label="Max consecutive CL days" field="max_consecutive_cl" />
          <RuleRow label="Min advance days for EL application" field="min_advance_days_el" />
          <RuleRow label="Carry forward Earned Leave to next year" field="carry_forward_el" type="switch" />
          <RuleRow label="Carry forward Casual Leave to next year" field="carry_forward_cl" type="switch" />
          <RuleRow label="Sandwich Rule (count weekends between leaves)" field="sandwich_rule" type="switch" />
          <RuleRow label="Leave allowed during probation" field="probation_leave_allowed" type="switch" />
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="py-3 px-4 pb-2">
          <CardTitle className="text-sm font-semibold">Policy Notice</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {editing ? (
            <Textarea
              value={rules.notice_text || ''}
              onChange={e => setRules({ ...rules, notice_text: e.target.value })}
              rows={3}
              className="text-sm"
              data-testid="rule-notice-text"
            />
          ) : (
            <p className="text-sm text-slate-600 leading-relaxed">{rules.notice_text || 'No policy notice set.'}</p>
          )}
        </CardContent>
      </Card>

      {/* Accrual Engine — admin only */}
      {admin && (
        <Card className="border-slate-200" data-testid="accrual-card">
          <CardHeader className="py-3 px-4 pb-2 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-slate-500" /> Monthly Leave Accrual
              </CardTitle>
              <p className="text-[11px] text-slate-400 mt-1">+1 EL and +0.5 CL credited on the 1st of every month. FY resets CL on Apr 1 (used kept for history). EL carries forward.</p>
            </div>
            <Button size="sm" onClick={handleRunAccrual} disabled={running} className="bg-[#0D2850] hover:bg-[#091B36] gap-1 h-8" data-testid="run-accrual-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${running ? 'animate-spin' : ''}`} />
              {running ? 'Running...' : 'Run Accrual Now'}
            </Button>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="flex items-center gap-2 mb-2">
              <History className="w-3.5 h-3.5 text-slate-500" />
              <p className="text-xs font-semibold text-slate-600">Recent Runs</p>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs ml-auto" onClick={loadLog} data-testid="refresh-accrual-log">Refresh</Button>
            </div>
            {logLoading ? <Skeleton className="h-20" /> : (accrualLog.runs.length === 0 ? (
              <p className="text-xs text-slate-400 italic py-4 text-center">No accrual runs yet. The first one will fire automatically on the 1st of next month, or click "Run Accrual Now".</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs">Started</TableHead>
                    <TableHead className="text-xs">Target Month</TableHead>
                    <TableHead className="text-xs text-center">Employees</TableHead>
                    <TableHead className="text-xs text-center">Months Credited</TableHead>
                    <TableHead className="text-xs text-center">FY Resets</TableHead>
                    <TableHead className="text-xs text-center">Skipped</TableHead>
                    <TableHead className="text-xs">Triggered By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accrualLog.runs.slice(0, 20).map((r, i) => (
                    <TableRow key={i} data-testid={`accrual-run-${i}`}>
                      <TableCell className="text-xs font-tabular">{(r.started_at || '').replace('T', ' ').slice(0, 16)}</TableCell>
                      <TableCell className="text-xs"><Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-[10px] font-mono">{r.target_month}</Badge></TableCell>
                      <TableCell className="text-xs text-center font-tabular">{r.total_employees ?? '-'}</TableCell>
                      <TableCell className="text-xs text-center font-tabular text-emerald-700 font-medium">{r.accrued ?? 0}</TableCell>
                      <TableCell className="text-xs text-center font-tabular">{r.fy_resets > 0 ? <Badge className="bg-violet-100 text-violet-700 text-[10px]">{r.fy_resets}</Badge> : '-'}</TableCell>
                      <TableCell className="text-xs text-center font-tabular text-slate-400">{r.skipped ?? 0}</TableCell>
                      <TableCell className="text-xs text-slate-500">{r.triggered_by || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Backfill Excess Leaves — admin only */}
      {admin && (
        <Card className="border-amber-200 bg-amber-50/30" data-testid="backfill-card">
          <CardHeader className="py-3 px-4 pb-2 flex flex-row items-start justify-between gap-2">
            <div>
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-amber-600" /> Re-classify Past Excess Leaves as LOP
              </CardTitle>
              <p className="text-[11px] text-slate-500 mt-1 leading-relaxed max-w-2xl">
                One-time backfill: scans every employee with a negative leave balance, finds the most recent approved leaves where balance went into the red, and re-classifies the excess days as LOP. <strong className="text-amber-700">After running, you should DELETE + REGENERATE any payroll run in the affected months</strong> from the Payroll page so the salary is corrected.
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleBackfill(true)} disabled={backfillRunning} className="h-8 gap-1" data-testid="backfill-dry-btn">
                {backfillRunning ? 'Running...' : 'Dry Run'}
              </Button>
              <Button size="sm" onClick={() => handleBackfill(false)} disabled={backfillRunning} className="bg-amber-600 hover:bg-amber-700 gap-1 h-8" data-testid="backfill-run-btn">
                <RefreshCw className={`w-3.5 h-3.5 ${backfillRunning ? 'animate-spin' : ''}`} />
                {backfillRunning ? 'Running...' : 'Run Backfill'}
              </Button>
            </div>
          </CardHeader>
          {backfillReport && (
            <CardContent className="px-4 pb-4">
              <div className="bg-white border border-amber-200 rounded p-3 text-xs space-y-2">
                <div className="flex flex-wrap gap-2">
                  <Badge className={`${backfillReport.dry_run ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'} text-[10px]`}>
                    {backfillReport.dry_run ? 'DRY RUN' : 'COMMITTED'}
                  </Badge>
                  <span className="text-slate-600">Employees touched: <strong>{backfillReport.employees_touched}</strong></span>
                  <span className="text-slate-600">Leaves marked: <strong>{backfillReport.leaves_marked || 0}</strong></span>
                  <span className="text-slate-600">LOP days: <strong className="text-red-600">{backfillReport.total_lop_days_marked || 0}</strong></span>
                </div>
                {(backfillReport.affected_payroll_runs || []).length > 0 && (
                  <div>
                    <span className="text-slate-600">📌 Payroll runs to regenerate: </span>
                    {backfillReport.affected_payroll_runs.map(m => (
                      <Badge key={m} className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px] font-mono ml-1">{m}</Badge>
                    ))}
                  </div>
                )}
                {(backfillReport.details || []).length > 0 && (
                  <details className="mt-2">
                    <summary className="text-xs cursor-pointer text-slate-700 font-medium">View details ({backfillReport.details.length} employees)</summary>
                    <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                      {backfillReport.details.map((d, i) => (
                        <li key={i} className="text-[11px] text-slate-600 border-l-2 border-amber-200 pl-2">
                          <strong>{d.employee_name}</strong> ({d.department}) —
                          {(d.buckets || []).map((b, j) => (
                            <span key={j} className="ml-1">
                              {b.leave_type}: bal {b.negative_balance}{b.would_re_classify ? `, would re-classify ${b.would_re_classify}` : ''}{b.absorbed ? `, absorbed ${b.absorbed}` : ''}{b.remaining_unfixable ? <span className="text-red-500"> (still {b.remaining_unfixable} unresolvable)</span> : ''}
                              {(b.affected_leaves || []).length > 0 && <span className="text-slate-400"> [{b.affected_leaves.length} leave(s) affected]</span>}
                            </span>
                          ))}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Reconcile All Balances — admin only */}
      {admin && (
        <Card className="border-blue-200 bg-blue-50/30" data-testid="reconcile-card">
          <CardHeader className="py-3 px-4 pb-2 flex flex-row items-start justify-between gap-2">
            <div>
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-blue-600" /> Reconcile All Leave Balances
              </CardTitle>
              <p className="text-[11px] text-slate-500 mt-1 leading-relaxed max-w-2xl">
                Force-syncs every employee&apos;s <strong>used</strong> and <strong>balance</strong> to match their actual approved leaves. Useful when a payslip shows N days adjusted but the Leave page&apos;s used count doesn&apos;t reflect it. Auto-runs at every payroll generation.
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleReconcileAll(true)} disabled={reconcileRunning} className="h-8 gap-1" data-testid="reconcile-dry-btn">
                {reconcileRunning ? 'Running...' : 'Dry Run'}
              </Button>
              <Button size="sm" onClick={() => handleReconcileAll(false)} disabled={reconcileRunning} className="bg-blue-600 hover:bg-blue-700 gap-1 h-8" data-testid="reconcile-run-btn">
                <RefreshCw className={`w-3.5 h-3.5 ${reconcileRunning ? 'animate-spin' : ''}`} />
                {reconcileRunning ? 'Running...' : 'Run Reconcile'}
              </Button>
            </div>
          </CardHeader>
          {reconcileReport && (
            <CardContent className="px-4 pb-4">
              <div className="bg-white border border-blue-200 rounded p-3 text-xs space-y-2">
                <div className="flex flex-wrap gap-2">
                  <Badge className={`${reconcileReport.dry_run ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'} text-[10px]`}>
                    {reconcileReport.dry_run ? 'DRY RUN' : 'COMMITTED'}
                  </Badge>
                  <span className="text-slate-600">Total scanned: <strong>{reconcileReport.total_scanned}</strong></span>
                  <span className="text-slate-600">Employees {reconcileReport.dry_run ? 'needing' : ''} reconcile: <strong>{reconcileReport.employees_reconciled}</strong></span>
                </div>
                {(reconcileReport.details || []).length > 0 && (
                  <details className="mt-2">
                    <summary className="text-xs cursor-pointer text-slate-700 font-medium">View details ({reconcileReport.details.length} employees)</summary>
                    <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                      {reconcileReport.details.map((d, i) => (
                        <li key={i} className="text-[11px] text-slate-600 border-l-2 border-blue-200 pl-2">
                          <strong>{d.employee_name}:</strong>
                          {Object.entries(d.changes || {}).map(([lt, ch]) => (
                            <span key={lt} className="ml-1">
                              {lt}: used <span className="text-red-500">{ch.used_before}</span> → <span className="text-emerald-600 font-medium">{ch.used_after}</span>, bal <span className="text-red-500">{ch.balance_before}</span> → <span className="text-emerald-600 font-medium">{ch.balance_after}</span>
                            </span>
                          ))}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
}
